/**
 * Per-frame stage timing for the composite render pipelines
 * (mosaicController.ts's CPU path, mosaicGlController.ts's GL path) — wraps
 * `performance.mark`/`performance.measure` (real DevTools-visible entries,
 * not just an ad-hoc `now()` diff) around each pipeline stage, and keeps a
 * small bounded rolling buffer of the most recent samples per stage so
 * app.ts's lab panel can show a one-line perf readout (dev/lab-only
 * consumer; this module itself has no such gate — recording a handful of
 * marks per frame is cheap enough to always run, and IS the actual
 * measurement infrastructure the rest of this file's callers rely on for
 * the caching/coalescing decisions below).
 *
 * `performance.clearMarks`/`clearMeasures` are called immediately after
 * each read (scoped to that call's own uniquely-numbered mark/measure
 * names only) so the browser's own PerformanceEntry buffer never grows
 * unbounded over a long-running scrub/loop session — this module's OWN
 * `samples` ring buffer is what actually backs perfSummaryLine(), not a
 * query against the browser's buffer.
 */
export type PerfStage = "manifest-resolve" | "fetch" | "decode" | "field-smooth" | "field-smooth-gpu" | "colorize" | "upload-draw";

export interface StageSample {
  stage: PerfStage;
  ms: number;
  /** Free-form context — e.g. "IFD0", "cache-hit", "sigma=1" — shown
   * alongside the timing in the lab panel readout. */
  meta?: string;
}

/** How many of the MOST RECENT samples (across all stages combined) to
 * retain — bounded so a long session's memory footprint stays flat. */
const RING_CAPACITY = 64;
const samples: StageSample[] = [];
let seq = 0;

function record(stage: PerfStage, ms: number, meta?: string): void {
  samples.push({ stage, ms, meta });
  if (samples.length > RING_CAPACITY) samples.shift();
}

/** Records an ALREADY-MEASURED duration directly — for stages whose timing
 * doesn't come from wrapping a synchronous/async JS callback (timeSync/
 * timeAsync above), e.g. mosaicGlCompositor.ts's GPU timer-query results
 * (EXT_disjoint_timer_query(_webgl2), read back asynchronously on a LATER
 * call, long after the actual GPU work — there is no JS callback left to
 * wrap by the time the result is known) or its lab-mode wall-clock+finish()
 * fallback when that extension isn't available. */
export function recordDuration(stage: PerfStage, ms: number, meta?: string): void {
  record(stage, ms, meta);
}

/** Times a synchronous stage (e.g. CPU colorize, GL upload+draw). */
export function timeSync<T>(stage: PerfStage, fn: () => T, meta?: string): T {
  const id = seq++;
  const startMark = `radar:${stage}:${id}:start`;
  const endMark = `radar:${stage}:${id}:end`;
  const measureName = `radar:${stage}:${id}`;
  performance.mark(startMark);
  const result = fn();
  performance.mark(endMark);
  const measure = performance.measure(measureName, startMark, endMark);
  record(stage, measure.duration, meta);
  performance.clearMarks(startMark);
  performance.clearMarks(endMark);
  performance.clearMeasures(measureName);
  return result;
}

/** Times an async stage (fetch/decode/field-smooth) — `meta` may be a
 * function of the AWAITED result (e.g. "cache-hit" vs "cache-miss", only
 * knowable after the stage resolves), since most of these decisions depend
 * on what actually happened, not just the inputs. */
export async function timeAsync<T>(stage: PerfStage, fn: () => Promise<T>, meta?: (result: T) => string | undefined): Promise<T> {
  const id = seq++;
  const startMark = `radar:${stage}:${id}:start`;
  const endMark = `radar:${stage}:${id}:end`;
  const measureName = `radar:${stage}:${id}`;
  performance.mark(startMark);
  const result = await fn();
  performance.mark(endMark);
  const measure = performance.measure(measureName, startMark, endMark);
  record(stage, measure.duration, meta?.(result));
  performance.clearMarks(startMark);
  performance.clearMarks(endMark);
  performance.clearMeasures(measureName);
  return result;
}

const STAGE_ORDER: PerfStage[] = ["manifest-resolve", "fetch", "decode", "field-smooth", "field-smooth-gpu", "colorize", "upload-draw"];

/** The most recent sample for each stage (later samples in the ring
 * override earlier ones for the same stage), in a fixed, readable order —
 * app.ts's lab panel status area. */
export function perfSummaryLine(): string {
  const latest = new Map<PerfStage, StageSample>();
  for (const s of samples) latest.set(s.stage, s);
  const parts = STAGE_ORDER.map((stage) => {
    const s = latest.get(stage);
    if (!s) return null;
    return `${stage}=${s.ms.toFixed(0)}ms${s.meta ? `(${s.meta})` : ""}`;
  }).filter((x): x is string => x !== null);
  return parts.length ? parts.join(" · ") : "no perf samples yet";
}

/** Test/debug hook — clears the rolling buffer. Not used by production code. */
export function resetPerfSamples(): void {
  samples.length = 0;
  seq = 0;
}
